import React from 'react'
import isEmpty from 'lodash/isEmpty'

// Local ASCII email check (general-rule) instead of `isValidEmail` from
// @ztechapps/common.utils: that barrel is not tree-shakeable and drags
// numeral + moment + libphonenumber into this marketing bundle (~2 MB), which
// also trips the rolldown-vite minifier. The server re-validates independently.
const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)

type ContactValues = {
  name: string
  school: string
  email: string
  phone: string
  message: string
}

type ContactErrors = Partial<Record<keyof ContactValues, string>>

const EMPTY_VALUES: ContactValues = { name: '', school: '', email: '', phone: '', message: '' }

const validate = (values: ContactValues): ContactErrors => {
  const errors: ContactErrors = {}

  if (isEmpty(values.name.trim())) errors.name = 'Required'
  if (isEmpty(values.school.trim())) errors.school = 'Required'
  if (isEmpty(values.email.trim())) errors.email = 'Required'
  else if (!isValidEmail(values.email.trim())) errors.email = 'Enter a valid email address.'
  if (isEmpty(values.phone.trim())) errors.phone = 'Required'
  if (isEmpty(values.message.trim())) errors.message = 'Required'

  return errors
}

export const ContactForm = () => {
  const [values, setValues] = React.useState<ContactValues>(EMPTY_VALUES)
  const [errors, setErrors] = React.useState<ContactErrors>({})
  const [submitting, setSubmitting] = React.useState(false)
  const [submitted, setSubmitted] = React.useState(false)
  const [serverError, setServerError] = React.useState<string | null>(null)

  const setField =
    (field: keyof ContactValues) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target
      setValues((prev) => ({ ...prev, [field]: value }))
    }

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setServerError(null)

    const nextErrors = validate(values)
    setErrors(nextErrors)
    if (!isEmpty(nextErrors)) return // invalid — block submit, no request sent

    try {
      setSubmitting(true)
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(values),
      })
      const result = await response.json().catch(() => ({}))

      if (!response.ok || !result?.success) {
        setServerError(result?.error ?? 'Something went wrong. Please try again later.')
        return
      }

      setSubmitted(true)
      setValues(EMPTY_VALUES)
    } catch {
      setServerError('Unable to send your message. Please check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="alert alert-success p-4 rounded-4" role="alert">
        <h5 className="mb-1">Thank you — your message is on its way.</h5>
        <p className="mb-0">Our team will get back to you shortly with a quote tailored to your school.</p>
      </div>
    )
  }

  return (
    <form className="p-4 border rounded-4 border-dashed" onSubmit={onSubmit} noValidate>
      <div className="row g-3">
        <div className="col-md-6">
          <label htmlFor="contact-name" className="form-label">
            Full Name
          </label>
          <input
            id="contact-name"
            type="text"
            autoComplete="name"
            className={`form-control py-2 ${errors.name ? 'is-invalid' : ''}`}
            placeholder="Enter your full name"
            value={values.name}
            onChange={setField('name')}
            disabled={submitting}
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>

        <div className="col-md-6">
          <label htmlFor="contact-school" className="form-label">
            School
          </label>
          <input
            id="contact-school"
            type="text"
            autoComplete="organization"
            className={`form-control py-2 ${errors.school ? 'is-invalid' : ''}`}
            placeholder="Enter your school name"
            value={values.school}
            onChange={setField('school')}
            disabled={submitting}
          />
          {errors.school && <div className="invalid-feedback">{errors.school}</div>}
        </div>

        <div className="col-md-6">
          <label htmlFor="contact-email" className="form-label">
            Email Address
          </label>
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            className={`form-control py-2 ${errors.email ? 'is-invalid' : ''}`}
            placeholder="Enter your email"
            value={values.email}
            onChange={setField('email')}
            disabled={submitting}
          />
          {errors.email && <div className="invalid-feedback">{errors.email}</div>}
        </div>

        <div className="col-md-6">
          <label htmlFor="contact-phone" className="form-label">
            Phone
          </label>
          <input
            id="contact-phone"
            type="tel"
            autoComplete="tel"
            className={`form-control py-2 ${errors.phone ? 'is-invalid' : ''}`}
            placeholder="Enter your phone number"
            value={values.phone}
            onChange={setField('phone')}
            disabled={submitting}
          />
          {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
        </div>

        <div className="col-12">
          <label htmlFor="contact-message" className="form-label">
            Message
          </label>
          <textarea
            id="contact-message"
            rows={5}
            className={`form-control py-2 ${errors.message ? 'is-invalid' : ''}`}
            placeholder="How can we help your school?"
            value={values.message}
            onChange={setField('message')}
            disabled={submitting}
          />
          {errors.message && <div className="invalid-feedback">{errors.message}</div>}
        </div>

        {serverError && (
          <div className="col-12">
            <div className="alert alert-danger py-2 mb-0" role="alert">
              {serverError}
            </div>
          </div>
        )}

        <div className="col-12 text-end">
          <button type="submit" className="btn btn-primary rounded-pill" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send Message'}
          </button>
        </div>
      </div>
    </form>
  )
}
